import { useEffect, useState } from 'react';
import { Download, Pencil, Trash2, Settings2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuCheckboxItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuItem,
} from '@/components/ui/dropdown';
import { StatusTag, ActiveTag } from './StatusTag';
import { api } from '@/lib/api';
import { COLUMN_DEFS, type ColumnKey, type Property } from '@/lib/types';
import { cn, formatDate, formatPrice } from '@/lib/utils';

const STORAGE_KEY = 'fangyuan.visibleColumns.v1';

function loadVisibleCols(): Set<ColumnKey> {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (raw) return new Set(JSON.parse(raw) as ColumnKey[]);
	} catch {}
	return new Set(COLUMN_DEFS.filter((c) => c.defaultVisible).map((c) => c.key));
}

interface Props {
	properties: Property[];
	loading: boolean;
	selected: Set<number>;
	onToggleSelect: (id: number) => void;
	onToggleSelectAll: (checked: boolean) => void;
	onEdit: (p: Property) => void;
	onDelete: (p: Property) => void;
}

export function PropertyTable({ properties, loading, selected, onToggleSelect, onToggleSelectAll, onEdit, onDelete }: Props) {
	const [visible, setVisible] = useState<Set<ColumnKey>>(loadVisibleCols);

	useEffect(() => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visible)));
	}, [visible]);

	const allSelected = properties.length > 0 && properties.every((p) => selected.has(p.id));
	const someSelected = properties.some((p) => selected.has(p.id));

	const toggleCol = (key: ColumnKey) => {
		setVisible((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	return (
		<div className="hidden md:flex md:flex-col flex-1 min-h-0">
			<div className="flex justify-end px-5 pt-3 -mb-1">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" size="sm" className="gap-1.5">
							<Settings2 size={13} /> 列
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="min-w-[180px]">
						<DropdownMenuLabel>显示列</DropdownMenuLabel>
						{COLUMN_DEFS.map((c) => (
							<DropdownMenuCheckboxItem
								key={c.key}
								checked={visible.has(c.key)}
								onCheckedChange={() => toggleCol(c.key)}
							>
								{c.label}
							</DropdownMenuCheckboxItem>
						))}
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={() => setVisible(new Set(COLUMN_DEFS.map((c) => c.key)))}>
							显示全部
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setVisible(new Set(COLUMN_DEFS.filter((c) => c.defaultVisible).map((c) => c.key)))}>
							恢复默认
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			<div className="overflow-x-auto px-5 pb-2 flex-1 scrollbar-thin">
				<table className="w-full border-collapse min-w-[720px]">
					<thead className="bg-green-100/70">
						<tr className="border-b border-green-200">
							<th className="w-9 text-left py-2.5 px-2.5">
								<Checkbox
									checked={allSelected ? true : someSelected ? 'indeterminate' : false}
									onCheckedChange={(v) => onToggleSelectAll(v === true)}
								/>
							</th>
							{COLUMN_DEFS.filter((c) => visible.has(c.key)).map((c) => (
								<th
									key={c.key}
									className={cn(
										'py-2.5 px-2.5 text-left text-[18px] font-semibold text-ink-2 whitespace-nowrap',
										(c.key === 'area' || c.key === 'total_price') && 'text-right'
									)}
								>
									{c.label}
								</th>
							))}
							<th className="w-16 text-right py-2.5 px-2.5 text-[18px] font-semibold text-ink-2">操作</th>
						</tr>
					</thead>
					<tbody>
						{loading && properties.length === 0 ? (
							<tr>
								<td colSpan={visible.size + 2} className="py-12 text-center text-ink-3">加载中...</td>
							</tr>
						) : properties.length === 0 ? (
							<tr>
								<td colSpan={visible.size + 2} className="py-12 text-center text-ink-3">没有匹配的房源</td>
							</tr>
						) : (
							properties.map((p) => (
								<tr
									key={p.id}
									className={cn(
										'border-b border-line transition-colors group',
										selected.has(p.id) ? 'bg-accent-soft' : 'hover:bg-[#f4f4f3]'
									)}
								>
									<td className="py-3 px-2.5 align-middle">
										<Checkbox checked={selected.has(p.id)} onCheckedChange={() => onToggleSelect(p.id)} />
									</td>
									{COLUMN_DEFS.filter((c) => visible.has(c.key)).map((c) => (
										<td
											key={c.key}
											className={cn(
												'py-3 px-2.5 text-[16.5px] text-ink-2 whitespace-nowrap align-middle',
												(c.key === 'area' || c.key === 'total_price') && 'text-right tabular',
												(c.key === 'remarks' || c.key === 'school_district') && 'max-w-[200px] overflow-hidden text-ellipsis'
											)}
										>
											{renderCell(p, c.key)}
										</td>
									))}
									<td className="py-3 px-2.5 text-right">
										<div className="inline-flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
											<button onClick={() => onEdit(p)} className="icon-btn" title="编辑">
												<Pencil size={13} />
											</button>
											<button
												onClick={() => onDelete(p)}
												className="icon-btn hover:!bg-red-50 hover:!text-danger"
												title="删除"
											>
												<Trash2 size={13} />
											</button>
										</div>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function renderCell(p: Property, key: ColumnKey): React.ReactNode {
	switch (key) {
		case 'room_number':
			return <span className="font-semibold text-ink tabular">{p.room_number}</span>;
		case 'status':
			return <StatusTag status={p.status} />;
		case 'is_active':
			return <ActiveTag active={p.is_active} />;
		case 'community_name':
			return <span className="font-medium text-ink">{p.community_name}</span>;
		case 'area':
			return p.area != null ? `${p.area} ㎡` : '—';
		case 'total_price':
			return <span className="font-semibold text-ink">{formatPrice(p.total_price, p.status)}</span>;
		case 'updated_at':
			return <span className="text-ink-3 text-[15px]">{formatDate(p.updated_at)}</span>;
		case 'video_key':
			return p.video_key ? (
				<a
					href={api.getPropertyVideoUrl(p.id)}
					className="inline-flex items-center gap-1 text-[15.5px] text-ink-2 hover:text-accent"
					title={p.video_name || '下载视频'}
				>
					<Download size={13} />
					下载
				</a>
			) : '—';
		case 'remarks':
		case 'school_district':
		case 'floor':
		case 'decoration':
		case 'garage':
		case 'build_time':
			return p[key] || '—';
	}
}
